import React from 'react';
import { ActivityLog } from '../../../Model/Activity/ActivityLog';
import { Activity } from '../../../Model/Activity/Activity';
import { BaseTrackingKey } from '../../../Model/ProgramPortfolio/TrackingKeys';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { SmartTrackingKeyInput } from './SmartTrackingKeyInput';
import {
  ScrollableContent,
  EditContainer,
  SelectedActivityHeader,
  SelectedActivityImage,
  SelectedActivityInfo,
  SelectedActivityName,
  BackButton,
  FormSection,
  FormRow,
  FormLabel,
  FormInput,
  SectionTitle,
} from './LogActivityModal.styles';

export type ActivityEditStepProps = {
  activityLog: ActivityLog;
  activity: Activity;
  language: string;
  date: string;
  startTime: string;
  endTime: string;
  trackingKeysToShow: BaseTrackingKey[];
  bmr?: number;
  onDataPointChange: (key: BaseTrackingKey, value: number) => void;
  onDateChange: (date: string) => void;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  onBack: () => void;
};

export const ActivityEditStep: React.FC<ActivityEditStepProps> = ({
  activityLog,
  activity,
  language,
  date,
  startTime,
  endTime,
  trackingKeysToShow,
  bmr,
  onDataPointChange,
  onDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onBack,
}) => {
  const { t } = useTrans();
  const activityName = useLocalized(activity?.name);

  return (
    <ScrollableContent>
      <EditContainer>
        <SelectedActivityHeader>
          <SelectedActivityImage>
            {activity?.firstSmallestMediaUrl && (
              <img src={activity.firstSmallestMediaUrl} alt={activityName} />
            )}
          </SelectedActivityImage>
          <SelectedActivityInfo>
            <SelectedActivityName>{activityName}</SelectedActivityName>
            <BackButton onClick={onBack}>← {t('activity.change', { defaultValue: 'Change activity' })}</BackButton>
          </SelectedActivityInfo>
        </SelectedActivityHeader>

        <FormSection>
          <SectionTitle>{t('activity.when', { defaultValue: 'When' })}</SectionTitle>
          <FormRow>
            <div>
              <FormLabel>{t('activity.date', { defaultValue: 'Date' })}</FormLabel>
              <FormInput type="date" value={date} onChange={(e) => onDateChange(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <FormLabel>{t('activity.start', { defaultValue: 'Start' })}</FormLabel>
                <FormInput type="time" value={startTime} onChange={(e) => onStartTimeChange(e.target.value)} />
              </div>
              <div>
                <FormLabel>{t('activity.end', { defaultValue: 'End' })}</FormLabel>
                <FormInput type="time" value={endTime} onChange={(e) => onEndTimeChange(e.target.value)} />
              </div>
            </div>
          </FormRow>
        </FormSection>

        {trackingKeysToShow.length > 0 && (
          <FormSection>
            <SectionTitle>{t('activity.data', { defaultValue: 'Activity Data' })}</SectionTitle>
            {trackingKeysToShow.map((key) => (
              <SmartTrackingKeyInput
                key={key}
                trackingKey={key}
                activityLog={activityLog}
                activity={activity}
                bmr={bmr}
                onDataPointChange={onDataPointChange}
              />
            ))}
          </FormSection>
        )}
      </EditContainer>
    </ScrollableContent>
  );
};
